import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, FileText, CheckCircle2, Clock, Plus, Trash2, Pencil, X, Video, AlertTriangle, Building2 } from "lucide-react";
import { ROLE_LABELS, OPERATIONAL_ROLES } from "@/lib/roles";
// Fix BH (2026-10-21): los manuales son para el personal. El backend acepta solo estos roles; antes el
// formulario ofrecía también Cliente, Cliente (administración) e Inquilino y guardar daba error 422.
const TRAINING_ROLE_KEYS = ["admin", "supervisor", ...OPERATIONAL_ROLES];
import { AttachmentPicker } from "@/components/AttachmentPicker";
import { fileSrc } from "@/lib/uploads";

/** Manual de procesos / Capacitaciones — accesible para todos los roles */
export default function Training() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const backLink = isAdmin ? "/admin" : user?.role === "supervisor" ? "/supervisor" : "/vigilador";

  const [docs, setDocs] = useState([]);
  const [clients, setClients] = useState([]);
  const [current, setCurrent] = useState(null); // doc abierto
  const [creating, setCreating] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);

  const load = async () => {
    try { const r = await api.get("/training/docs"); setDocs(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, [isAdmin]);

  const clientName = (cid) => clients.find((c) => c.id === cid)?.name || cid;

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar documento?")) return;
    try { await api.delete(`/training/docs/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div style={{ padding: isAdmin ? "20px 32px" : "8px 16px", maxWidth: 1100, margin: "0 auto" }}>
      <Link to={backLink} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 12, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14}/> Volver</Link>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 900, margin: 0, color: "#0A0A0A", letterSpacing: "-0.02em" }}>Manual de procesos</h1>
          <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>Protocolos y capacitaciones · Lectura y firma obligatoria</div>
        </div>
        {isAdmin && (
          <button onClick={() => setCreating(true)} className="btn-primary" style={{ padding: "10px 16px" }} data-testid="train-add"><Plus size={14} style={{ marginRight: 6 }}/>Nuevo documento</button>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="train-list">
        {docs.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "var(--invict-text-muted)" }}><FileText size={28} style={{ opacity: 0.4, margin: "0 auto 6px" }}/><div style={{ fontSize: 13 }}>Sin documentos cargados.</div></div>}
        {docs.map((d) => (
          <div key={d.id} style={{ padding: "14px 16px", borderBottom: "1px solid var(--invict-bone-3)", display: "flex", gap: 12, alignItems: "center" }} data-testid={`train-row-${d.id}`}>
            <FileText size={22} style={{ color: "#BF3437", flexShrink: 0 }}/>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 900, fontSize: 14, color: "#0A0A0A" }}>{d.title}</div>
              {d.description && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 2 }}>{d.description}</div>}
              <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 4, letterSpacing: "0.05em" }}>Asignado a: {(d.assign_roles || []).map((r) => ROLE_LABELS[r] || r).join(", ")}</div>
              <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 2, letterSpacing: "0.05em", display: "flex", alignItems: "center", gap: 4 }} data-testid={`train-objectives-${d.id}`}>
                <Building2 size={10}/>
                {(d.assign_client_ids || []).length === 0
                  ? "Todos los objetivos"
                  : d.assign_client_ids.map((cid) => clientName(cid)).join(", ")}
              </div>
            </div>
            {!isAdmin && (
              d.my_signature ? (
                <span className="badge green" data-testid={`train-signed-${d.id}`}><CheckCircle2 size={10} style={{ marginRight: 3 }}/>Firmado</span>
              ) : d.my_previous_signature ? (
                <span className="badge amber" data-testid={`train-resign-${d.id}`}><AlertTriangle size={10} style={{ marginRight: 3 }}/>Debe re-firmar</span>
              ) : (
                <span className="badge amber" data-testid={`train-pending-${d.id}`}><Clock size={10} style={{ marginRight: 3 }}/>Pendiente</span>
              )
            )}
            <button onClick={() => setCurrent(d)} className="btn-secondary" style={{ padding: "8px 12px", fontSize: 11 }} data-testid={`train-open-${d.id}`}>Abrir</button>
            {isAdmin && <button onClick={() => setEditingDoc(d)} style={{ background: "none", border: "none", color: "#0A0A0A", cursor: "pointer", padding: 6 }} data-testid={`train-edit-${d.id}`}><Pencil size={16}/></button>}
            {isAdmin && <button onClick={() => remove(d.id)} style={{ background: "none", border: "none", color: "#BF3437", cursor: "pointer", padding: 6 }} data-testid={`train-del-${d.id}`}><Trash2 size={16}/></button>}
          </div>
        ))}
      </div>

      {current && <DocViewer doc={current} onClose={() => { setCurrent(null); load(); }} />}
      {creating && <DocForm clients={clients} onClose={() => { setCreating(false); load(); }} />}
      {editingDoc && <DocForm clients={clients} existing={editingDoc} onClose={() => { setEditingDoc(null); load(); }} />}
    </div>
  );
}

function DocViewer({ doc, onClose }) {
  const { user } = useAuth();
  const canvasRef = useRef(null);
  const [signatureText, setSignatureText] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);
  const [signatures, setSignatures] = useState([]);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (isAdmin || user?.role === "supervisor") {
      api.get(`/training/docs/${doc.id}/signatures`).then((r) => setSignatures(r.data)).catch(() => {});
    }
  }, [doc.id, isAdmin, user?.role]);

  const drawing = useRef(false);
  const startDraw = (e) => {
    drawing.current = true;
    const c = canvasRef.current; const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current; const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y); ctx.strokeStyle = "#0A0A0A"; ctx.lineWidth = 2; ctx.stroke();
  };
  const stopDraw = () => { drawing.current = false; };
  const clearCanvas = () => {
    const c = canvasRef.current; const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
  };

  const sign = async () => {
    if (!signatureText.trim()) { toast.error("Tipeá tu nombre completo"); return; }
    setBusy(true);
    try {
      let signature_image_url = null;
      const c = canvasRef.current;
      if (c) {
        // canvas to blob to upload
        const blob = await new Promise((res) => c.toBlob(res, "image/png"));
        if (blob && blob.size > 200) {  // ignore empty canvases
          const form = new FormData();
          form.append("file", new File([blob], "firma.png", { type: "image/png" }));
          const r = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
          signature_image_url = r.data.url;
        }
      }
      await api.post(`/training/docs/${doc.id}/sign`, { signature_text: signatureText, signature_image_url });
      toast.success("Firma registrada");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} data-testid="train-viewer">
      <div style={{ background: "#FFFBF4", maxWidth: 720, width: "100%", maxHeight: "90vh", overflowY: "auto", borderRadius: 4 }}>
        <div style={{ padding: "18px 20px", borderBottom: "2px solid #BF3437", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: "0.2em", color: "#BF3437", fontWeight: 800, textTransform: "uppercase" }}>Documento</div>
            <h2 style={{ fontSize: 18, fontWeight: 900, margin: 0, color: "#0A0A0A" }}>{doc.title}</h2>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#0A0A0A" }} data-testid="train-close"><X size={20}/></button>
        </div>
        <div style={{ padding: 20 }}>
          {doc.description && <p style={{ fontSize: 13, color: "var(--invict-text-muted)", marginTop: 0 }}>{doc.description}</p>}
          {doc.content && (
            <div style={{ background: "#fff", padding: 16, borderLeft: "3px solid #BF3437", whiteSpace: "pre-wrap", fontSize: 13, lineHeight: 1.6, color: "#0A0A0A" }} data-testid="train-content">{doc.content}</div>
          )}
          {(doc.files || []).length > 0 && (
            <div style={{ marginTop: 14 }} data-testid="train-file-block">
              <div style={{ fontSize: 10, fontWeight: 800, color: "var(--invict-text-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>Archivos adjuntos ({doc.files.length})</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {doc.files.map((f, i) => (
                  f.kind === "video" ? (
                    <video key={i} src={fileSrc(f.url)} controls style={{ width: "100%", maxHeight: 360, borderRadius: 4, background: "#000", display: "block" }} data-testid={`train-file-video-${i}`} />
                  ) : (
                    <a key={i} href={fileSrc(f.url)} target="_blank" rel="noreferrer" className="btn-secondary"
                      style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 16px", alignSelf: "flex-start" }} data-testid={`train-file-doc-${i}`}>
                      <FileText size={14} /> Abrir / descargar{f.name ? ` · ${f.name}` : " archivo"}
                    </a>
                  )
                ))}
              </div>
            </div>
          )}

          {!isAdmin && doc.my_previous_signature && !doc.my_signature && (
            <div style={{ marginTop: 20, padding: 12, background: "#fef3c7", border: "1px solid #f59e0b", display: "flex", gap: 10, alignItems: "flex-start" }} data-testid="train-resign-notice">
              <AlertTriangle size={18} style={{ color: "#92400e", flexShrink: 0, marginTop: 1 }}/>
              <div style={{ fontSize: 12, color: "#78350f" }}>
                Este documento se actualizó desde tu última firma ({new Date(doc.my_previous_signature.signed_at).toLocaleString("es-AR")}). Es necesario que lo vuelvas a leer y firmar.
              </div>
            </div>
          )}

          {!isAdmin && !doc.my_signature && (
            <div style={{ marginTop: 24, padding: 16, background: "#fff", border: "2px solid #BF3437" }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#BF3437", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>Confirmación de lectura y firma</div>
              <p style={{ fontSize: 12, color: "#0A0A0A", margin: "0 0 12px" }}>
                Declaro haber leído y comprendido este documento, comprometiéndome a cumplir los protocolos y procedimientos indicados.
              </p>
              <div className="field"><label>Nombre completo *</label>
                <input value={signatureText} onChange={(e) => setSignatureText(e.target.value)} data-testid="train-sig-text"/>
              </div>
              <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginBottom: 4 }}>Firma manuscrita (opcional)</div>
              <canvas ref={canvasRef} width={500} height={140}
                onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
                onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
                style={{ width: "100%", maxWidth: 500, height: 140, border: "1px dashed var(--invict-bone-3)", background: "#fff", borderRadius: 3, touchAction: "none" }} data-testid="train-sig-canvas"/>
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button type="button" onClick={clearCanvas} className="btn-secondary" style={{ padding: "8px 14px", fontSize: 11 }}>Borrar firma</button>
                <button onClick={sign} disabled={busy} className="btn-primary" style={{ flex: 1, padding: "10px 14px" }} data-testid="train-sign-btn">{busy ? "Firmando..." : "Leí y acepto · Firmar"}</button>
              </div>
            </div>
          )}

          {!isAdmin && doc.my_signature && (
            <div style={{ marginTop: 24, padding: 14, background: "#dcfce7", border: "1px solid #16a34a", display: "flex", gap: 10, alignItems: "center" }}>
              <CheckCircle2 size={20} style={{ color: "#16a34a", flexShrink: 0 }}/>
              <div>
                <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>Documento firmado</div>
                <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>Firmado el {new Date(doc.my_signature.signed_at).toLocaleString("es-AR")} por {doc.my_signature.signature_text}</div>
              </div>
            </div>
          )}

          {(isAdmin || user?.role === "supervisor") && (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A", marginBottom: 8 }}>Firmas registradas ({signatures.length})</div>
              <div className="card" style={{ padding: 0, overflow: "hidden", maxHeight: 240, overflowY: "auto" }}>
                {signatures.length === 0 && <div style={{ padding: 12, textAlign: "center", fontSize: 12, color: "var(--invict-text-muted)" }}>Sin firmas todavía</div>}
                {signatures.map((s) => (
                  <div key={s.id} style={{ padding: "10px 14px", borderBottom: "1px solid var(--invict-bone-3)", fontSize: 12, display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <div>
                      <b>{s.signature_text}</b> · <span style={{ color: "var(--invict-text-muted)" }}>{s.user_role}</span>
                      {(s.document_version || 1) !== (doc.version || 1) && <span className="badge amber" style={{ marginLeft: 6, fontSize: 9 }}>versión anterior (v{s.document_version || 1})</span>}
                    </div>
                    <div style={{ color: "var(--invict-text-muted)", fontSize: 11 }}>{new Date(s.signed_at).toLocaleString("es-AR")}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DocForm({ clients, existing, onClose }) {
  const isEdit = !!existing;
  const [form, setForm] = useState({
    title: existing?.title || "",
    description: existing?.description || "",
    content: existing?.content || "",
  });
  const [fileAtt, setFileAtt] = useState(
    (existing?.files || []).map((f) => ({ url: f.url, kind: f.kind, filename: f.name }))
  );
  const [roles, setRoles] = useState({
    admin: existing?.assign_roles?.includes("admin") ?? false,
    supervisor: existing?.assign_roles?.includes("supervisor") ?? true,
    vigilador: existing?.assign_roles?.includes("vigilador") ?? true,
    operario_limpieza: existing?.assign_roles?.includes("operario_limpieza") ?? false,
    custodio: existing?.assign_roles?.includes("custodio") ?? false,
  });
  const [allClients, setAllClients] = useState((existing?.assign_client_ids || []).length === 0);
  const [selectedClients, setSelectedClients] = useState(existing?.assign_client_ids || []);
  const [requiresResign, setRequiresResign] = useState(false);
  const [busy, setBusy] = useState(false);

  const toggleClient = (cid) => {
    setSelectedClients((prev) => prev.includes(cid) ? prev.filter((c) => c !== cid) : [...prev, cid]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.content.trim() && fileAtt.length === 0) {
      toast.error("Cargá contenido de texto, un archivo, o ambos");
      return;
    }
    setBusy(true);
    try {
      const assign_roles = Object.keys(roles).filter((r) => roles[r]);
      const files = fileAtt.map((f) => ({ url: f.url, kind: f.kind === "video" ? "video" : "document", name: f.filename }));
      const assign_client_ids = allClients ? [] : selectedClients;
      if (isEdit) {
        await api.patch(`/training/docs/${existing.id}`, { ...form, assign_roles, files, assign_client_ids, requires_resign: requiresResign });
        toast.success("Documento actualizado");
      } else {
        await api.post("/training/docs", { ...form, assign_roles, files, assign_client_ids });
        toast.success("Documento creado");
      }
      onClose();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <form onSubmit={submit} style={{ background: "#FFFBF4", maxWidth: 600, width: "100%", maxHeight: "90vh", overflowY: "auto", borderRadius: 4 }} data-testid="train-create-form">
        <div style={{ padding: "16px 20px", borderBottom: "2px solid #BF3437", display: "flex", justifyContent: "space-between" }}>
          <h2 style={{ fontSize: 18, fontWeight: 900, margin: 0, color: "#0A0A0A" }}>{isEdit ? "Editar documento" : "Nuevo documento"}</h2>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={20}/></button>
        </div>
        <div style={{ padding: 20 }}>
          <div className="field"><label>Título *</label><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="train-create-title"/></div>
          <div className="field"><label>Descripción corta</label><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}/></div>
          <div className="field"><label>Contenido (markdown / texto)</label>
            <textarea rows={8} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Protocolo, instrucciones..." data-testid="train-create-content"/>
          </div>
          <div className="field"><label>Archivos adjuntos (varios documentos y/o videos)</label>
            <AttachmentPicker value={fileAtt} onChange={setFileAtt} accept="document_video" multiple max={6} label="Subir archivos" />
            <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 4 }}>
              Máx. 25 MB por PDF/Word, 50 MB por video. Podés cargar texto, varios archivos, o ambos.
            </div>
          </div>
          <div className="field"><label>Asignar a (roles)</label>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {TRAINING_ROLE_KEYS.map((r) => (
                <label key={r} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" checked={roles[r]} onChange={(e) => setRoles({ ...roles, [r]: e.target.checked })} data-testid={`train-role-${r}`}/>
                  {ROLE_LABELS[r]}
                </label>
              ))}
            </div>
          </div>
          <div className="field"><label>Asignar a (objetivos)</label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", marginBottom: 6 }}>
              <input type="checkbox" checked={allClients} onChange={(e) => setAllClients(e.target.checked)} data-testid="train-clients-all"/>
              Todos los objetivos
            </label>
            {!allClients && (
              <div style={{ border: "1px solid var(--invict-bone-3)", borderRadius: 4, padding: 10, maxHeight: 180, overflowY: "auto" }} data-testid="train-clients-picker">
                {clients.length === 0 && <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>No hay objetivos creados todavía.</div>}
                {clients.map((c) => (
                  <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", padding: "3px 0" }}>
                    <input type="checkbox" checked={selectedClients.includes(c.id)} onChange={() => toggleClient(c.id)} data-testid={`train-client-${c.id}`}/>
                    {c.name}
                  </label>
                ))}
              </div>
            )}
            <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 4 }}>
              Se le muestra automáticamente al personal que trabaja en esos objetivos (ronda asignada, cronograma o servicio activo).
            </div>
          </div>
          {isEdit && (
            <div className="field" style={{ background: "#fef3c7", border: "1px solid #f59e0b", padding: 10, borderRadius: 3 }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, cursor: "pointer" }}>
                <input type="checkbox" checked={requiresResign} onChange={(e) => setRequiresResign(e.target.checked)} data-testid="train-requires-resign"/>
                <span>Este cambio es de fondo — el personal que ya firmó debe volver a firmar. Las firmas anteriores NO se borran, quedan en el historial.</span>
              </label>
            </div>
          )}
          <button type="submit" disabled={busy} className="btn-primary" style={{ width: "100%", padding: 12 }} data-testid={isEdit ? "train-edit-submit" : "train-create-submit"}>
            {busy ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear documento"}
          </button>
        </div>
      </form>
    </div>
  );
}
